import React, { useState, useMemo } from 'react';
import L from 'leaflet';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';


const calculatePolygonAreaSqft = (coords) => {
    if (!coords || coords.length === 0) return 0;
    const isArrayOfRings = Array.isArray(coords[0]) && Array.isArray(coords[0][0]);
    const rings = isArrayOfRings ? coords : [coords];
    if (rings[0].length < 3) return 0;

    const ringAreaSqMeters = (ring) => {
        let area = 0;
        const RADIUS = 6378137;
        for (let i = 0; i < ring.length; i++) {
            const p1 = ring[i];
            const p2 = ring[(i + 1) % ring.length];
            const lat1 = p1[1] * Math.PI / 180, lng1 = p1[0] * Math.PI / 180;
            const lat2 = p2[1] * Math.PI / 180, lng2 = p2[0] * Math.PI / 180;
            area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
        }
        return Math.abs(area * RADIUS * RADIUS / 2.0);
    };

    let areaSqMeters = ringAreaSqMeters(rings[0]);
    for (let i = 1; i < rings.length; i++) {
        areaSqMeters -= ringAreaSqMeters(rings[i]);
    }
    areaSqMeters = Math.max(areaSqMeters, 0);
    return areaSqMeters * 10.7639;
};


const PolygonCoverage = ({ polygons, setPolygons, toggleVisibility, toggleAllVisibility, zoomToFeature,
    handleColorChange
 }) => {
    const [footerUnit, setFooterUnit] = useState('sqft');
    const [expandedGroups, setExpandedGroups] = useState({});

    // --- AREA CALCULATION LOGIC (With Safety Fallback) ---
   const ringArea = (ring) => {
    if (!ring || ring.length < 3) return 0;
    const latLngs = ring.map(c => L.latLng(c[1], c[0])); // note: lat, lng order for Leaflet
    if (L.GeometryUtil && typeof L.GeometryUtil.geodesicArea === 'function') {
        return Math.abs(L.GeometryUtil.geodesicArea(latLngs));
    }
    let area = 0;
    const RADIUS = 6378137;
    for (let i = 0; i < latLngs.length; i++) {
        let p1 = latLngs[i];
        let p2 = latLngs[(i + 1) % latLngs.length];
        area += (p2.lng - p1.lng) * Math.PI / 180 * (2 + Math.sin(p1.lat * Math.PI / 180) + Math.sin(p2.lat * Math.PI / 180));
    }
    return Math.abs(area * RADIUS * RADIUS / 2.0);
};

const calculateArea = (coords) => {
    if (!coords || coords.length === 0) return { sqft: 0, acre: 0 };

    // Detect whether coords is a single flat ring [[lng,lat],...]
    // or an array of rings [[outer],[hole1],...]
    const isArrayOfRings = Array.isArray(coords[0]) && Array.isArray(coords[0][0]);
    const rings = isArrayOfRings ? coords : [coords];

    if (rings[0].length < 3) return { sqft: 0, acre: 0 };

    let areaInSqMeters = ringArea(rings[0]); // outer ring
    for (let i = 1; i < rings.length; i++) {
        areaInSqMeters -= ringArea(rings[i]); // subtract holes
    }
    areaInSqMeters = Math.max(areaInSqMeters, 0);

    return { 
        sqft: areaInSqMeters * 10.7639, 
        acre: areaInSqMeters / 4046.86 
    };
};

    const groupedPolygons = useMemo(() => {
        return polygons.reduce((acc, p) => {
            const { sqft, acre } = calculateArea(p.coords);
            const enrichedPoly = { ...p, sqft, acre };
            if (!acc[p.name]) acc[p.name] = [];
            acc[p.name].push(enrichedPoly);
            return acc;
        }, {});
    }, [polygons]);

    const totals = useMemo(() => {
        let totalSqft = 0;
        let totalAcre = 0;
        polygons.forEach(p => {
            const { sqft, acre } = calculateArea(p.coords);
            totalSqft += sqft;
            totalAcre += acre;
        });
        return { 
            sqft: totalSqft.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
acre: totalAcre.toFixed(2)
        };
    }, [polygons]);

    const allVisible = polygons.every(p => p.visible);
    const totalFeatureCount = polygons.length;

    const handleGlobalUnitToggle = () => {
        setFooterUnit(prev => prev === 'sqft' ? 'acre' : 'sqft');
    };

    // const handleColorChange = (name, newColor) => {
    //     setPolygons(prev => prev.map(p => p.name === name ? { ...p, color: newColor } : p));
    // };

    const toggleGroupExpansion = (name) => {
        setExpandedGroups(prev => ({ ...prev, [name]: !prev[name] }));
    };

    const toggleGroupVisibility = (name, targetVisible) => {
        const group = groupedPolygons[name];
        group.forEach(p => {
            if (p.visible !== targetVisible) toggleVisibility(p.id, 'polygon');
        });
    };

    return (
        <div className="coverage-section" id="polygon-section">
            <div className="coverage-header">
                <div className="coverage-header-left">
                    <i className="fas fa-draw-polygon" /> 
                    <span>Polygon Coverage</span>
                </div>

                <div className="total-badge">
                    <i className="fas fa-calculator" />
                    <span>{totalFeatureCount} Features</span>
                </div>

                <div className="coverage-header-right">
                    <button className="section-unit-toggle" onClick={handleGlobalUnitToggle}>
                        <i className="fas fa-exchange-alt" />
                    </button>
                    <button className={`section-eye-toggle ${allVisible ? 'active' : ''}`} onClick={() => toggleAllVisibility(!allVisible)}>
                        <i className={`fas ${allVisible ? 'fa-eye' : 'fa-eye-slash'}`} />
                    </button>
                </div>
            </div>

            <table className="coverage-table">
    <tbody>
        {Object.keys(groupedPolygons).map(groupName => {
            const group = groupedPolygons[groupName];
            const groupVisible = group.every(p => p.visible);
            const isExpanded = expandedGroups[groupName];
            
            // Calculate totals for the main heading
            const groupSqft = group.reduce((sum, p) => sum + p.sqft, 0);
            const groupAcre = group.reduce((sum, p) => sum + p.acre, 0);

            return (
                <React.Fragment key={groupName}>
                    {/* --- SINGLE MAIN HEADING (e.g., "Lawn") --- */}
                    <tr className="group-header-row">
                        <td className="feature-name-cell">
                            <div className="feature-left">
                                <button 
                                    className={`collapse-btn ${isExpanded ? 'active' : ''}`} 
                                    onClick={() => toggleGroupExpansion(groupName)}
                                >
                                     {isExpanded ? <ChevronDown size={15} /> :  <ChevronRight size={15} />}  
                                  
                                </button>
                                
                                <div className="feature-color-box" style={{ background: group[0].color, cursor: 'pointer', position: 'relative' }}>
                                    <input 
                                        type="color" 
                                        value={group[0].color} 
                                        onChange={(e) => handleColorChange(groupName, e.target.value)}
                                        style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer', width: '100%', height: '100%' }}
                                    />
                                </div>

                                <div className="feature-name">
                                    {groupName} <span className="count-badge">({group.length})</span>
                                </div>
                            </div>
                        </td>
                        <td className="feature-value-cell">
                            <div className="feature-right">
                                <div className="feature-value-container">
                                    <span className="feature-value">
                                        {/* {footerUnit === 'sqft' ? Math.round(groupSqft).toLocaleString() : groupAcre.toFixed(3)} */}
                                        {footerUnit === 'sqft' ? groupSqft.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : groupAcre.toFixed(2)}
                                    </span>
                                    <span className="feature-unit">{footerUnit === 'sqft' ? 'sq ft' : 'acre'}</span>
                                </div>
                                
                                <button 
                                    className="eye-toggle-small" 
                                    onClick={() => zoomToFeature(group.flatMap(p => p.coords))} 
                                    title="Zoom to All"
                                >
                                    <i className="fas fa-crosshairs" />
                                </button>

                                <button 
                                    className={`eye-toggle-small ${groupVisible ? 'active' : ''}`} 
                                    onClick={() => toggleGroupVisibility(groupName, !groupVisible)}
                                >
                                    <i className={`fas ${groupVisible ? 'fa-eye' : 'fa-eye-slash'}`} />
                                </button>
                            </div>
                        </td>
                    </tr>

                    {/* --- NESTED SUB-ITEMS (Lawn 1, Lawn 2, Lawn 3) --- */}
                    {isExpanded && group.map((p, index) => (
                        <tr key={p.id} className="sub-feature-row">
                            <td className="feature-name-cell" style={{ paddingLeft: '40px' }}>
                                <div className="feature-left">
                                    <div className="feature-name" style={{ fontSize: '0.8rem', color: '#666' }}>
                                        <i className="fas fa-reply fa-rotate-180" style={{ marginRight: '8px', opacity: 0.5 }} />
                                        {groupName} {index + 1}
                                    </div>
                                </div>
                            </td>
                            <td className="feature-value-cell">
                                <div className="feature-right">
                                    <div className="feature-value-container">
                                        <span className="feature-value" style={{ fontSize: '0.8rem' }}>
                                            {footerUnit === 'sqft' ? p.sqft.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : p.acre.toFixed(2)}
                                        </span>
                                    </div>

                                    <button 
                                        className="eye-toggle-small" 
                                        onClick={() => zoomToFeature(p.coords)}
                                        title="Jump to Location"
                                    >
                                        <i className="fas fa-map-marker-alt" style={{ fontSize: '0.75rem' }} />
                                    </button>

                                    <button 
                                        className={`eye-toggle-small ${p.visible ? 'active' : ''}`} 
                                        onClick={() => toggleVisibility(p.id, 'polygon')}
                                    >
                                        <i className={`fas ${p.visible ? 'fa-eye' : 'fa-eye-slash'}`} style={{ fontSize: '0.75rem' }} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </React.Fragment>
            );
        })}
    </tbody>
</table>

            <div className="total-area-row">
                <div className="total-area-content">
                    <div className="total-area-label"><i className="fas fa-calculator" /> Total Polygon Area</div>
                    <div className="total-area-value">
                        <span className="total-value">{footerUnit === 'sqft' ? totals.sqft : totals.acre}</span>
                        <span className="total-unit">{footerUnit === 'sqft' ? 'sq ft' : 'acre'}</span>
                        <button className="total-unit-toggle" onClick={handleGlobalUnitToggle}>
                            <i className="fas fa-exchange-alt" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PolygonCoverage;